import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

import { StrictMode, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { configureUnauthorizedHandler } from './api/client';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { handleUnauthorizedResponse } from './auth/sessionExpiry';
import { shouldRetryQuery } from './lib/queryRetry';
import { router } from './router';
import { theme } from './theme';

const queryClient = new QueryClient({
  defaultOptions: {
    // retry predicate (not a flat count): a 401 (dead/expired token) fails the
    // same way every time, so retrying just floods the API — see
    // lib/queryRetry.ts. Everything else keeps the previous `retry: 1`.
    queries: { retry: shouldRetryQuery, staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

/** Re-renders `RouterProvider` with fresh `context.auth` whenever auth state
 * changes, so route guards (`_authenticated.tsx`'s `beforeLoad`) always see the
 * current `isAuthenticated`/tenant/role — see the "Authenticated Routes" pattern in
 * the TanStack Router docs. */
function AppRouter() {
  const auth = useAuth();

  // Wires src/api/client.ts's generic 401 hook (fires on every dead/expired
  // token, from any query or mutation — 2026-09-18 bug) to
  // auth/sessionExpiry.ts's redirect-once handling. Lives here (not inside
  // AuthContext.tsx) because it needs the real `router` singleton to navigate
  // and read the current location — pulling that into AuthContext.tsx would
  // pull in routeTree.gen.ts's real route tree too, which several auth tests
  // (e.g. login.test.tsx) deliberately avoid by mocking `@tanstack/react-router`
  // to a minimal stub. Ref-guarded so it registers exactly once; `auth.logout`
  // is stable ([] deps in AuthContext), so capturing it on the first render is
  // safe for the app's whole lifetime.
  const unauthorizedWiredRef = useRef(false);
  if (!unauthorizedWiredRef.current) {
    unauthorizedWiredRef.current = true;
    configureUnauthorizedHandler(() => {
      handleUnauthorizedResponse({
        pathname: router.state.location.pathname,
        currentHref: router.state.location.href,
        clearSession: auth.logout,
        redirectToLogin: (returnTo) => {
          void router.navigate({ to: '/login', search: { redirect: returnTo } });
        },
      });
    });
  }

  return <RouterProvider router={router} context={{ auth }} />;
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootElement).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <Notifications position="top-right" />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);
